import React, { useEffect, useState } from "react";
import { FiMail, FiLock, FiArrowLeft } from "react-icons/fi";
import { VscEyeClosed } from "react-icons/vsc";
import { RxEyeOpen } from "react-icons/rx";
import logo from "../assets/new images/logo.png";
import { useNavigate, NavLink } from "react-router-dom";
import axios from "axios";
import apiKey from "../config/apiKey";
import { toast } from "react-toastify";

const AdminLoginForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({ email: "", password: "" });
  // "credentials" | "twofa" — a 2FA-enabled account gets { requires_2fa:
  // true } back from /admin/login (no token yet) instead of logging in
  // directly; this second step re-sends the same email+password alongside
  // the authenticator code to /admin/login/verify-2fa.
  const [step, setStep] = useState("credentials");
  const [code, setCode] = useState("");
  const navigate = useNavigate();

  const handleChange = (e) =>
    setFormData((s) => ({ ...s, [e.target.name]: e.target.value }));

  const completeLogin = (data) => {
    localStorage.setItem("edo-admin", JSON.stringify(data));
    toast.success("Login Successfully", {
      position: "bottom-right",
      autoClose: 3500,
      theme: "colored",
      style: { backgroundColor: "#2A3A54", color: "#fff" },
    });
    const roleId = Number(data.admin?.role_id);
    navigate(roleId === 6 ? "/school/dashboard" : roleId === 7 ? "/teacher/dashboard" : "/admins/home");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});
    try {
      const res = await axios.post(`${apiKey}/api/edolutions/admin/login`, formData);
      const data = res.data.data;
      if (data?.requires_2fa) {
        setStep("twofa");
        return;
      }
      completeLogin(data);
    } catch (err) {
      if (err?.response?.data?.errors) setErrors(err.response.data.errors);
      else setErrors({ general: "An unexpected error occurred" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify2fa = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});
    try {
      const res = await axios.post(`${apiKey}/api/edolutions/admin/login/verify-2fa`, {
        email: formData.email,
        password: formData.password,
        code,
      });
      completeLogin(res.data.data);
    } catch (err) {
      setErrors({ general: err?.response?.data?.message || "That code is incorrect or expired." });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const raw = localStorage.getItem("edo-admin");
    if (raw) {
      try {
        const data = JSON.parse(raw);
        const roleId = Number(data.admin?.role_id);
        navigate(roleId === 6 ? "/school/dashboard" : roleId === 7 ? "/teacher/dashboard" : "/admins/home");
      } catch { navigate("/admins/home"); }
    }
  }, [navigate]);

  return (
    <div className="relative min-h-screen w-full grid place-items-center overflow-hidden">
      {/* soft peach background + blurred blobs */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#FFF4EE] via-[#FFF8F4] to-[#FFEFE7]" />
      <div className="pointer-events-none absolute -left-24 -top-24 w-[38rem] h-[38rem] rounded-full bg-[#FFB899]/25 blur-3xl" />
      <div className="pointer-events-none absolute -right-28 bottom-0 w-[42rem] h-[42rem] rounded-full bg-[#FFD9C9]/30 blur-3xl" />

      {/* content container */}
      <div className="relative w-full max-w-xl px-5">
        {/* back to home */}
        <div className="mb-2 md:pl-11">
          <NavLink
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#111827]"
          >
            <FiArrowLeft className="h-4 w-4" />
            Back to Home
          </NavLink>
        </div>

        {/* card */}
        <div className="max-w-md mx-auto rounded-3xl border border-[#F7C9B7] bg-white/90 shadow-[0_10px_40px_-15px_rgba(17,24,39,0.15)] px-6 py-8 md:px-10 md:py-10">
          {/* mini logo pill */}
          <div className="mx-auto -mt-3 mb-5 w-16 h-16 grid place-items-center rounded-2xl bg-white shadow-xl shadow-orange-600/30">
            <img src={logo} alt="logo" className="w-10 h-10 object-contain" />
          </div>

          {/* heading */}
          <div className="text-center">
            <h1 className="text-3xl md:text-[32px] font-extrabold">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]">
                Welcome Back!
              </span>
            </h1>
            {/* <p className="mt-2 text-sm text-[#6B7280]">
              Continue your journey to find the perfect mentor
            </p> */}
          </div>

          {/* 2FA step */}
          {step === "twofa" ? (
            <form onSubmit={handleVerify2fa} className="mt-8 space-y-5">
              {errors.general && (
                <div className="rounded-md bg-red-50 text-red-700 text-sm px-3 py-2">
                  {errors.general}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-2">
                  Enter your 6-digit authenticator code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="000000"
                  className="w-full rounded-xl border border-[#E5E7EB] bg-white px-4 py-3 text-[20px] tracking-[0.5em] text-center outline-none focus:border-[#EE5F2B] focus:ring-2 focus:ring-[#EE5F2B]/20 transition"
                />
              </div>
              <button
                type="submit"
                disabled={isLoading || code.length !== 6}
                className={`w-full rounded-xl py-3.5 text-white font-semibold shadow-sm transition
                  ${isLoading || code.length !== 6 ? "opacity-60 cursor-not-allowed" : "hover:shadow-md"}
                  bg-[#EE5F2B]
                `}
              >
                {isLoading ? "Verifying…" : "Verify & Sign In"}
              </button>
              <button
                type="button"
                onClick={() => { setStep("credentials"); setCode(""); setErrors({}); }}
                className="w-full text-sm text-[#6B7280] hover:text-[#111827]"
              >
                ← Back to login
              </button>
            </form>
          ) : (
          /* form */
          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            {errors.general && (
              <div className="rounded-md bg-red-50 text-red-700 text-sm px-3 py-2">
                {errors.general}
              </div>
            )}

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-[#111827] mb-2">
                Email Address
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-3 grid place-items-center">
                  <FiMail className="h-4 w-4 text-[#9CA3AF]" />
                </span>
                <input
                  type="email"
                  name="email"
                  onChange={handleChange}
                  placeholder="Enter your email"
                  className="w-full rounded-xl border border-[#E5E7EB] bg-white pl-10 pr-3 py-3 text-[15px] outline-none focus:border-[#EE5F2B] focus:ring-2 focus:ring-[#EE5F2B]/20 transition"
                />
              </div>
              {errors.email && (
                <p className="text-xs text-red-600 mt-1">{errors.email}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-[#111827] mb-2">
                Password
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-3 grid place-items-center">
                  <FiLock className="h-4 w-4 text-[#9CA3AF]" />
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  onChange={handleChange}
                  placeholder="Enter your password"
                  className="w-full rounded-xl border border-[#E5E7EB] bg-white pl-10 pr-11 py-3 text-[15px] outline-none focus:border-[#EE5F2B] focus:ring-2 focus:ring-[#EE5F2B]/20 transition"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute inset-y-0 right-2 px-2 grid place-items-center text-[#6B7280] hover:text-[#111827]"
                >
                  {showPassword ? <RxEyeOpen className="h-5 w-5" /> : <VscEyeClosed className="h-5 w-5" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-red-600 mt-1">{errors.password}</p>
              )}
              <div className="mt-2 text-right">
                <NavLink to="/forgot" className="text-xs text-[#EE5F2B] hover:underline">
                  Forgot password?
                </NavLink>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full rounded-xl py-3.5 text-white font-semibold shadow-sm transition
                ${isLoading ? "opacity-60 cursor-not-allowed" : "hover:shadow-md"}
                bg-[#EE5F2B]
              `}
            >
              {isLoading ? (
                <span className="inline-flex items-center gap-2">
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0A12 12 0 000 12h4z"
                    />
                  </svg>
                  Signing in…
                </span>
              ) : (
                "Sign In"
              )}
            </button>

            {/* Footer line */}
            {/* <p className="text-center text-sm text-[#6B7280]">
              Don’t have an account?{" "}
              <NavLink to="/signup" className="text-[#EE5F2B] font-semibold hover:underline">
                Sign up
              </NavLink>
            </p> */}
          </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLoginForm;
